import { useId, useSyncExternalStore } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CONTRASTES, FONTES, MOVIMENTOS, PREFERENCIAS_PADRAO, ehPreferenciasValidas,
  type PreferenciasA11y,
} from '../../../shared/acessibilidade';
import { aplicarEGravar } from '../../lib/acessibilidade';
import { api, euQuery } from '../../api/cliente';
import { Icone } from '../Icone';
import { useAvisar } from './Avisador';
import { useFecharAoNavegar } from './fecharAoNavegar';

/*
 * O que vale agora é o que está no <html>: o script do index.html, a tela de
 * conta e este painel escrevem lá. Ler daí mantém os três em acordo sem estado
 * duplicado.
 */
function lerAplicadas(): PreferenciasA11y {
  const d = document.documentElement.dataset;
  const p = { contraste: d['contraste'], fonte: d['fonte'], movimento: d['movimento'] };
  return ehPreferenciasValidas(p) ? p : PREFERENCIAS_PADRAO;
}
let cache: PreferenciasA11y = PREFERENCIAS_PADRAO;
function instantaneo(): PreferenciasA11y {
  const atual = lerAplicadas();
  if (atual.contraste !== cache.contraste || atual.fonte !== cache.fonte || atual.movimento !== cache.movimento) cache = atual;
  return cache;
}
function assinar(avisar: () => void) {
  const o = new MutationObserver(avisar);
  o.observe(document.documentElement, { attributes: true, attributeFilter: ['data-contraste', 'data-fonte', 'data-movimento'] });
  return () => o.disconnect();
}

const ROTULOS = {
  fonte: { padrao: 'Normal', grande: 'Grande', muito_grande: 'Maior' },
  contraste: { padrao: 'Normal', alto: 'Alto' },
  movimento: { padrao: 'Com animações', reduzido: 'Reduzir' },
} as const;

/**
 * Ajustes de leitura no topo de toda tela, logado ou não: quem mais precisa
 * de letra grande é justamente quem não vai achar a opção dentro de "Conta".
 * Muda na hora (prévia ao vivo); logado, também salva na conta.
 */
export function AjustesDeLeitura() {
  const id = useId();
  const prefs = useSyncExternalStore(assinar, instantaneo, () => PREFERENCIAS_PADRAO);
  const { data: usuario } = useQuery(euQuery);
  const qc = useQueryClient();
  const avisar = useAvisar();
  const salvar = useMutation({
    mutationFn: (p: PreferenciasA11y) => api.put<{ preferencias: PreferenciasA11y }>('/eu/preferencias', p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['eu'] }),
    onError: () => avisar('Não deu para salvar na sua conta agora. Neste aparelho o ajuste já vale.', 'erro'),
  });

  function mudar(parcial: Partial<PreferenciasA11y>) {
    const novo = { ...prefs, ...parcial };
    aplicarEGravar(novo);
    if (usuario) salvar.mutate(novo);
  }

  const idPainel = `${id}-ajustes`;
  const ref = useFecharAoNavegar<HTMLDivElement>();
  return (
    <>
      <button type="button" className="acao-topo ancora-ajustes" popoverTarget={idPainel} aria-label="Ajustes de leitura">
        <Icone nome="ajustes" />
        <span aria-hidden="true">Leitura</span>
      </button>
      <div ref={ref} id={idPainel} popover="auto" className="flutuante flutuante-ajustes" aria-labelledby={`${id}-titulo`} role="dialog">
        <div className="flutuante-cabeca">
          <h2 id={`${id}-titulo`}>Ajustes de leitura</h2>
          <button type="button" className="acao-link" popoverTarget={idPainel} popoverTargetAction="hide">
            <Icone nome="fechar" />
            <span>Fechar</span>
          </button>
        </div>
        <div className="flutuante-corpo">
          <Escolha id={`${id}-fonte`} titulo="Tamanho da letra" valores={FONTES} rotulos={ROTULOS.fonte}
            atual={prefs.fonte} aoMudar={(fonte) => mudar({ fonte })} />
          <Escolha id={`${id}-contraste`} titulo="Contraste" valores={CONTRASTES} rotulos={ROTULOS.contraste}
            atual={prefs.contraste} aoMudar={(contraste) => mudar({ contraste })} />
          <Escolha id={`${id}-movimento`} titulo="Animações" valores={MOVIMENTOS} rotulos={ROTULOS.movimento}
            atual={prefs.movimento} aoMudar={(movimento) => mudar({ movimento })} />
          <p className="flutuante-nota">
            {usuario ? 'Fica salvo na sua conta e vale em qualquer aparelho.' : 'Fica salvo neste aparelho.'}{' '}
            <Link to="/acessibilidade">Tudo sobre acessibilidade</Link>
          </p>
        </div>
      </div>
    </>
  );
}

function Escolha<T extends string>({ id, titulo, valores, rotulos, atual, aoMudar }: {
  id: string;
  titulo: string;
  valores: readonly T[];
  rotulos: Record<T, string>;
  atual: T;
  aoMudar: (v: T) => void;
}) {
  return (
    <fieldset className="segmentado-grupo">
      <legend className="segmentado-titulo">{titulo}</legend>
      <div className="segmentado">
        {valores.map((v) => (
          <label key={v}>
            <input type="radio" name={id} value={v} checked={atual === v} onChange={() => aoMudar(v)} />
            {rotulos[v]}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
